// API Service for Synapses-OD Backend

const SERVER_BASE_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
const API_BASE_URL = import.meta.env.VITE_API_URL || `${SERVER_BASE_URL}/api`;

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  meta?: {
    timestamp: string;
    requestId: string;
  };
}

export interface RuntimeOverview {
  featureFlags: Record<string, boolean>;
  availability: Record<string, boolean>;
  sessions: number;
  outputJobs: number;
  telemetryCount: number;
}

export interface ShellSession {
  id: string;
  user: string;
  shell_type: 'web' | 'mobile' | 'ops';
  identity_ref?: string | null;
  feature_flags: Record<string, boolean>;
  created_at: string;
}

export interface BrainResult {
  plan: string[];
  content_spec: Record<string, unknown>;
  workflow_spec: Record<string, unknown>;
  render_spec: Record<string, unknown>;
  fallback_used: boolean;
  backend: 'ouroboros' | 'simd' | 'dual' | 'llm';
  semantic_resolution: {
    source: 'arabic' | 'embedding' | 'hybrid';
    meaning_map: {
      normalized_text: string;
      keywords: string[];
      arabic_root_candidates: string[];
      embedding_backup_used: boolean;
    };
    confidence: number;
  };
}

export interface OutputJob {
  id: string;
  backend: 'axiom' | 'diffusion' | 'other';
  status: 'queued' | 'running' | 'complete' | 'failed';
  artifacts: Array<{ kind: string; label: string; url: string }>;
  identity_ref?: string | null;
  artifact_type?: string | null;
  fallback_used: boolean;
  error?: string | null;
  created_at: string;
  updated_at: string;
}

// Generic fetch wrapper
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  
  return response.json();
}

// System Status
export const getHealth = async () => {
  const response = await fetch(`${SERVER_BASE_URL}/health`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json() as Promise<ApiResponse<{ status: string; initialized?: boolean }>>;
};
export const getStatus = () => fetchApi<{
  initialized: boolean;
  identity: { alignmentScore: number; constraintCount: number };
  knowledgeGraph: { totalNodes: number; totalEdges: number };
  tokenUsage: Record<string, { used: number; max: number; percent: number }>;
}>('/status');

// Identity Layer
export const getIdentity = () => fetchApi<{
  manifest: {
    voice: { tone: string[]; vocabulary: string[]; prohibitedWords: string[] };
    values: string[];
    positioning: string;
  };
  constraints: Array<{ id: string; type: string; rule: string }>;
  alignmentScore: number;
}>('/identity');

// Skills Layer
export const listSkills = () => fetchApi<Array<{
  id: string;
  name: string;
  type: string;
  identitySensitive: boolean;
  validationRules: Array<{ field: string; required: boolean; type: string }>;
}>>('/skills');

export const getSkill = (name: string) => fetchApi(`/skills/${name}`);

export const executeSkill = (
  name: string,
  agentRole: string,
  payload: Record<string, unknown>,
  context?: Record<string, unknown>
) => fetchApi<{
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
  metadata: { executionTimeMs: number; identityAligned: boolean; tokensUsed?: number };
}>(`/skills/${name}/execute`, {
  method: 'POST',
  body: JSON.stringify({ agentRole, payload, context }),
});

// Personas Layer
export const listPersonas = () => fetchApi<Array<{
  id: string;
  role: string;
  traits: {
    openness: number;
    conscientiousness: number;
    extraversion: number;
    agreeableness: number;
    neuroticism: number;
  };
  riskTolerance: number;
  formality: number;
  empathy: number;
}>>('/personas');

export const getPersona = (role: string) => fetchApi(`/personas/${role}`);

// Orchestrator Layer
export const orchestrate = (requests: Array<{
  skillName: string;
  input: { skillName: string; context: Record<string, unknown>; payload: Record<string, unknown> };
  preferredAgent?: string;
}>) => fetchApi<{
  taskId: string;
  results: Array<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: string;
    metadata: { executionTimeMs: number; identityAligned: boolean };
  }>;
  conflicts: Array<{
    id: string;
    agents: string[];
    type: string;
    description: string;
  }>;
  budgetReport: {
    consumedTokens: Record<string, number>;
    violations: string[];
    utilizationPercent: Record<string, number>;
  };
  checkpointViolations: number;
  totalExecutionTimeMs: number;
}>('/orchestrate', {
  method: 'POST',
  body: JSON.stringify({ requests }),
});

export const getTokenUsage = () => fetchApi<Record<string, { used: number; max: number; percent: number }>>('/orchestrator/token-usage');

// Strategy Layer
export const getCausalGraph = () => fetchApi<{
  nodes: Array<{ id: string; type: string; name: string; currentValue: number; baselineValue: number }>;
  edges: Array<{ from: string; to: string; weight: number; confidence: number }>;
}>('/strategy/causal-graph');

export const getAgentImpact = (agent: string) => fetchApi<{
  agent: string;
  score: number;
  confidenceInterval: [number, number];
  contributingPaths: string[];
}>(`/strategy/impact/${agent}`);

export const getAgentRankings = () => fetchApi<Array<{
  agent: string;
  impact: number;
  rank: number;
}>>('/strategy/rankings');

export const simulateScenario = (changes: Record<string, number>) => fetchApi<Record<string, number>>('/strategy/simulate', {
  method: 'POST',
  body: JSON.stringify({ changes }),
});

// Knowledge Graph
export const getKGStats = () => fetchApi<{
  totalNodes: number;
  totalEdges: number;
  nodesByType: Record<string, number>;
}>('/kg/stats');

export const searchKG = (query: string, type?: string) => fetchApi<Array<{
  id: string;
  type: string;
  properties: Record<string, unknown>;
}>>(`/kg/nodes?query=${encodeURIComponent(query)}${type ? `&type=${type}` : ''}`);

export const getKGNode = (id: string) => fetchApi(`/kg/nodes/${id}`);

export const createKGNode = (type: string, properties: Record<string, unknown>) => fetchApi<{
  id: string;
  type: string;
  properties: Record<string, unknown>;
}>('/kg/nodes', {
  method: 'POST',
  body: JSON.stringify({ type, properties }),
});

export const getContextPack = (personId: string) => fetchApi<{
  person?: { id: string; properties: Record<string, unknown> };
  deals: Array<{ id: string; properties: Record<string, unknown> }>;
  activities: Array<{ id: string; properties: Record<string, unknown> }>;
  learningHistory: Array<{ id: string; properties: Record<string, unknown> }>;
  signals: Array<{ id: string; properties: Record<string, unknown> }>;
  brandRules: Array<{ id: string; properties: Record<string, unknown> }>;
}>(`/kg/context/${personId}`);

// DSS Interface
export const queryDSS = (
  query: string,
  mode: 'sales_rep' | 'manager' | 'customer' | 'admin' = 'sales_rep',
  context?: Record<string, unknown>
) => fetchApi<{
  answer: string;
  causalScore: number;
  confidenceInterval: [number, number];
  contributingAgents: string[];
  reasoning: string;
}>('/dss/query', {
  method: 'POST',
  body: JSON.stringify({ query, mode, context }),
});

// Agent Status
export const getAgentStatuses = () => fetchApi<Array<{
  role: string;
  status: 'idle' | 'busy' | 'error' | 'halted';
  skills: string[];
  tokensUsed: number;
  tokensMax: number;
  utilization: number;
}>>('/agents/status');

// Shipment-first runtime
export const getRuntimeOverview = () => fetchApi<RuntimeOverview>('/runtime/overview');

export const createRuntimeSession = (payload: {
  user?: string;
  shell_type: 'web' | 'mobile' | 'ops';
  identity_ref?: string | null;
}) => fetchApi<ShellSession>('/runtime/session', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const getRuntimeFeatureFlags = () => fetchApi<Record<string, boolean>>('/runtime/feature-flags');

export const executeRuntimeBrain = (payload: {
  intent: string;
  goal: string;
  identity_ref?: string | null;
  artifact_type?: string | null;
  route_policy?: 'auto' | 'ouroboros' | 'simd' | 'dual' | 'llm';
  session_id?: string | null;
}) => fetchApi<BrainResult>('/runtime/brain/execute', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const createRuntimeOutputJob = (payload: {
  identity_ref?: string | null;
  artifact_type?: string | null;
  prompt: string;
  preferred_backend?: 'axiom' | 'diffusion' | 'other';
}) => fetchApi<OutputJob>('/runtime/output-jobs', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const getRuntimeOutputJob = (id: string) => fetchApi<OutputJob>(`/runtime/output-jobs/${id}`);

export const getRuntimeTelemetry = () => fetchApi<Array<{
  ts: string;
  primary_attempted: string;
  fallback_triggered: boolean;
  reason: string;
  user_visible_impact: string;
}>>('/runtime/fallback-telemetry');

export const applyRuntimePullback = (target: string) => fetchApi<Record<string, boolean>>('/runtime/pullback', {
  method: 'POST',
  body: JSON.stringify({ target }),
});
